<template>
  <b-modal
    :id="modalId"
    size="lg"
    no-enforce-focus
    hide-footer
    hide-header
    body-class="p-3"
    @shown="onShown"
    @hidden="shown = false"
  >
    <div class="text-right">
      <b-button variant="link" size="sm" class="text-muted" @click="onClickClose">
        <i class="fa fa-fw fa-times"></i>
      </b-button>
    </div>

    <b-row v-if="!!event" align-h="center">
      <b-col class="col-12 col-md-10">
        <b-row>
          <b-col class="col-12 col-md">
            <b-form-group :label="trans('title')">
              <b-form-input v-model="title" />
            </b-form-group>
          </b-col>
          <b-col class="col-12 col-md-auto no-left-padding">
            <b-row>
              <b-col>
                <b-form-group :label="trans('color')">
                  <b-button
                    v-for="c in colors"
                    :key="c.color"
                    variant="none"
                    size="sm"
                    class="mr-2"
                    :style="`background-color: ${c.color};`"
                    @click="color = c.color"
                  >
                    <i :class="`fa fa-fw ${c.color == color ? 'fa-check' : ''}`"></i>
                  </b-button>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row align-v="center">
              <b-col class="col-auto pr-0">
                <div>
                  <b-form-group class="mb-0">
                    <color-picker v-model="custom_color" @input="updateColor"></color-picker>
                  </b-form-group>
                </div>
              </b-col>
              <b-col class="pl-0">
                {{ trans('custom') }}
                <i :class="`fa fa-fw ${custom_color == color ? 'fa-check text-success' : ''}`"></i>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
       
        <b-form-group :label="trans('date')">
          <edit-event-date
            :organization="organization"
            :start="start"
            @start="start = $event"
            :end="end"
            @end="end = $event"
            :all-day="allDay"
            @all-day="allDay = $event"
          ></edit-event-date>
        </b-form-group>
        <b-form-group :label="`${trans('subtitle')} (${trans('optional').toLowerCase()})`">
          <b-form-input v-model="subtitle" />
        </b-form-group>
        <b-form-group :label="`${trans('additional_information_in_popup')} (${trans('optional').toLowerCase()})`">
          <html-textarea
            :organization="organization"
            v-model="description"
            for-microsite
            show-insert-form-document
          ></html-textarea>
        </b-form-group>

        <b-row>
          <b-col class="col-12 col-md">
            <b-form-group :label="`${trans('form')} (${trans('optional').toLowerCase()})`">
              <form-dropdown
                v-if="shown"
                :organization="organization"
                v-model="recipient_form_id"
                :initial_page_url="button_url"
                :placeholder="trans('choose_a_form')"
                mode="forms"
                nullable
                @list="onSelectForm"
                :disabled="busy"
              ></form-dropdown>
            </b-form-group>
          </b-col>
          <b-col v-if="!!recipient_form_id" class="col-12 col-md-auto pl-0">
            <b-form-group :label="trans('anchor_target')">
              <b-form-select
                v-model="button_target"
                :options="button_target_options"
                :disabled="busy"
              ></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group v-if="!!recipient_form_id" :label="trans('button')">
          <b-form-input
            v-model="button_text"
            :disabled="busy"
          />
        </b-form-group>

        <b-form-group :label="trans('address') + ' (' + trans('optional').toLowerCase() + ')'">
          <b-form-input v-model="address" :disabled="busy" />
        </b-form-group>
        <div v-if="!map_image_url && address && !busy" class="mb-2">
          <b-button variant="outline-primary" size="sm" @click="loadMapImageUrl">
            <i class="fa fa-fw fa-eye"></i>
            {{ trans('show_map') }}
          </b-button>
        </div>
        <div v-if="map_image_url" class="mb-4">
          <b-img fluid :src="map_image_url" />
          <div v-if="address && address != old_address && !busy" class="mt-2">
            <b-button variant="outline-primary" size="sm" @click="loadMapImageUrl">
              <i class="fa fa-fw fa-redo"></i>
              {{ trans('refresh_map') }}
            </b-button>
          </div>
          <div v-if="address && address == old_address && !busy" class="mt-2">
            <b-button variant="outline-danger" size="sm" @click="map_image_url = null">
              <i class="fa fa-fw fa-eye-slash"></i>
              {{ trans('hide_map') }}
            </b-button>
          </div>
        </div>

        <p v-if="busy">
          <i class="fa fa-fw fa-circle-notch fa-spin"></i>
          {{ trans('please_wait') }}
        </p>
        <p v-if="error" class="text-danger">
          {{ error }}
        </p>
      </b-col>
    </b-row>

    <b-row class="mt-4">
      <b-col>
        <b-button v-if="!!event && event.id !== null" variant="outline-danger" class="mr-2" @click="onClickDelete">
          {{ trans('delete') }}
        </b-button>
      </b-col>
      <b-col class="col-auto">
        <b-button variant="light" class="mr-2" @click="onClickCancel">
          {{ trans('cancel') }}
        </b-button>
        <b-button variant="branded" @click="onClickSave">
          {{ trans('save') }}
        </b-button>
      </b-col>
    </b-row>
  </b-modal>
</template>

<style>
  .no-left-padding .color-picker {
    padding-left: 0 !important;
  }
</style>

<script>
  import HtmlTextarea from './../../../common/components/HtmlTextarea'
  import Datetime from 'vue-bootstrap-datetimepicker'
  import EditEventDate from './../calendar/EditEventDate'
  import FormDropdown from './../list/ListDropdown'
  import ColorPicker from './../../../common/components/ColorPicker'

  export default {
    props: {
      organization: Object,
      event: Object,
      modalId: {
        type: String,
        default: 'page-calendar-edit-event-modal',
      },
    },
    components: {
      HtmlTextarea,
      Datetime,
      EditEventDate,
      FormDropdown,
      ColorPicker
    },
    data() {
      return {
        trans: trans,
        shown: false,
        busy: false,
        error: null,
        dateRangeStr: _.dateRangeStr,
        timeRangeStr: _.timeRangeStr,
        // These are FullCalendar Event properties (except for description)
        id: this.event ? this.event.id : null,
        title: this.event ? this.event.title : null, // title
        subtitle: this.event ? this.event.subtitle || null : null, // subtitle
        description: this.event ? this.event.description : null, // popup description
        start: this.event ? this.event.start : null,
        end: this.event ? this.event.end : null,
        allDay: this.event ? this.event.allDay : false,
        color: this.event ? this.event.color || '#B8DCEE' : '#B8DCEE',
        textColor: this.event ? this.getTextColor(this.event) : '#0f0f0f',
        colors: _.calendarColors,
        address: this.event ? this.event.address || null : null,
        old_address: this.event ? this.event.address || null : null,
        map_image_url: this.event ? this.event.map_image_url || false : false,
        recipient_form_id: null,
        button_text: trans('fill_form'),
        button_url: null,
        button_target: 'popup',
        button_target_options: [
          {text: trans('anchor_target_popup'), value: 'popup'},
          {text: trans('anchor_target_blank'), value: '_blank'},
          {text: trans('anchor_target_self'), value: null}, // self
        ],
        recipient_form: null,
        custom_color: '#22b24c'
      }
    },
    computed: {
      isAdmin() {
        return !!this.organization && !!this.organization.auth_user
      },
      serialized() {
        return {
          id: this.id,
          title: this.title,
          subtitle: this.subtitle,
          description: this.description,
          start: this.start,
          end: this.end,
          allDay: this.allDay,
          color: this.color,
          textColor: this.textColor,
          address: this.address,
          map_image_url: this.map_image_url,
          recipient_form_id: this.recipient_form_id,
          button_text: this.button_text,
          button_url: this.button_url,
          button_target: this.button_target,
        }
      },
    },
    methods: {
      onShown() {
        this.id = this.event.id
        this.title = this.event.title
        this.subtitle = this.event ? this.event.subtitle || null : null
        this.description = this.event.description
        this.start = this.event.start
        this.end = this.event.end
        this.allDay = this.event ? this.event.allDay : false
        this.color = this.event ? this.event.color || '#B8DCEE' : '#B8DCEE'
        this.textColor = this.event ? this.getTextColor(this.event) : '#0f0f0f'
        this.address = this.event ? this.event.address || null : null
        this.old_address = this.address
        this.map_image_url = this.event ? this.event.map_image_url || false : false
        this.recipient_form_id = this.event ? this.event.recipient_form_id || null : null
        this.button_text = this.event ? this.event.button_text || trans('fill_form') : trans('fill_form')
        this.button_url = this.event ? this.event.button_url || null : null
        this.button_target = this.event ? this.event.button_target || 'popup' : 'popup'
        this.shown = true
        this.custom_color = !_.includes(_.map(this.colors, c => c.color), this.event?.color) ? this.event.color : '#22b24c'
      },
      onSelectForm(recipient_form, context) {
        this.recipient_form = recipient_form
        this.button_url = recipient_form ? recipient_form.page_url : null
        if (!!context && context.source == 'user' && !!recipient_form) {
          this.button_text = recipient_form.name
        }
      },
      onClickDelete() {
        this.$bvModal.msgBoxConfirm(trans('delete_confirm'), {
          title: trans('delete'),
          okTitle: trans('delete'),
          okVariant: 'danger',
          cancelTitle: trans('cancel'),
        })
        .then(confirmed => {
          if (!confirmed) return
          this.$emit('delete', this.serialized)
          this.$bvModal.hide(this.modalId)
        })
      },
      onClickSave() {
        console.log(this.serialized)
        this.$emit('save', this.serialized)
        this.$bvModal.hide(this.modalId)
      },
      onClickCancel() {
        this.$bvModal.hide(this.modalId)
      },
      onClickClose() {
        this.$bvModal.hide(this.modalId)
      },
      loadMapImageUrl() {
        this.map_image_url = null
        if (!this.address) return
        this.busy = true
        this.error = null
        const url = `/organization/${this.organization.id}/location/map_image_url?address=${encodeURIComponent(this.address)}`
        axios.get(url)
        .then(response => {
          this.busy = false
          this.old_address = this.address
          this.map_image_url = response.data.map_image_url
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      updateColor(e) {
        this.color = e
      },
      getTextColor() {
        return this.color ? _.hexContrastColor(this.color) : '#0f0f0f'
      }
    },
    mounted() {
      window.pageCalendarEditEventModal = this
    },
  }
</script>